import { useCallback, useEffect, useMemo, useState } from "react";

import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import { Column, Row } from "@tanstack/table-core";
import { get, last, merge } from "lodash-es";
import { twJoin } from "tailwind-merge";

import { DatasetRow } from "src/api/types";
import { Icon } from "src/base-components/Icon";
import { PropertyDefinitionOutputTypeEnum } from "src/clients/features-control";
import { CellWrapper } from "src/datasets/components/CellWrapper";
import { EntityPropertyEditorCell } from "src/datasets/components/EntityPropertyEditorCell";
import { getErrorDetails } from "src/datasets/components/errorUtils";
import { JSONPill } from "src/datasets/components/JSONPill";
import { RelationCellDropdown } from "src/datasets/components/RelationCellDropdown";
import { RelationPill } from "src/datasets/components/RelationJSONEditor";
import { RowUpdateFunction } from "src/datasets/DatasetTable/DatasetEditTable";
import { FloatingWindow } from "src/datasets/DatasetTable/FloatingWindow";
import { useDatasetEditTableActions } from "src/datasets/DatasetTable/stores";
import { JSONValue } from "src/datasets/DatasetTable/types";
import { ValidationResult } from "src/datasets/DatasetTable/validators";
import { Tooltip } from "src/design-system/Tooltip";
import { TypeIcon } from "src/entities/entityView/EntityDetailsSidePane";
import {
  findSchema,
  getOrderedProperties,
} from "src/entities/entityView/utils";
import { EntitySchemaProperty, useEntitySchemas } from "src/entities/queries";
import { useWorkspaceContext } from "src/router/routerContextHooks";

type FloatingEntityEditorProps = {
  disabled: boolean;
  error: ValidationResult | undefined;
  column: Column<DatasetRow>;
  invalid: boolean;
  warning: boolean;
  propValue: Record<string, JSONValue> | undefined;
  onChange: RowUpdateFunction;
  relationSchema: string;
  value: string;
  type: string;
  row: Row<DatasetRow>;
  selected: boolean;
  editing: boolean;
};

const FEATURES_PROPERTY = {
  _type: PropertyDefinitionOutputTypeEnum.OBJECT,
  _display_name: "Features",
  _indexed: false,
};

export const FloatingEntityEditor: React.FC<FloatingEntityEditorProps> = ({
  value,
  disabled,
  error,
  invalid,
  warning,
  propValue,
  onChange,
  relationSchema: relation,
  type,
  column,
  row,
  selected,
  editing,
}) => {
  const {
    pool,
    goToRelation,
    currentEditor,
    reset: resetEntitiesPool,
  } = useEntitiesPool(relation);
  const { workspace } = useWorkspaceContext();
  const { reset } = useDatasetEditTableActions();

  const {
    setSelected: setSelectedProperty,
    setEditing: setEditingProperty,
    reset: resetPropertyCellState,
    isEditing,
    isSelected,
  } = usePropertyCellState();

  useEffect(() => {
    if (!editing) {
      resetPropertyCellState();
      resetEntitiesPool();
    }
  }, [editing, resetPropertyCellState, resetEntitiesPool]);

  const { data: entitySchemas } = useEntitySchemas({
    baseUrl: workspace.base_url!,
  });

  const relationSchema = useMemo(() => {
    const schema = findSchema(currentEditor.relation, entitySchemas);
    return schema
      ? merge({}, schema, {
          properties: {
            features: FEATURES_PROPERTY,
          },
        })
      : null;
  }, [currentEditor.relation, entitySchemas]);

  const properties = useMemo(() => {
    return relationSchema ? getOrderedProperties(relationSchema) : [];
  }, [relationSchema]);

  const children = (
    <CellWrapper
      disabled={disabled}
      editing={editing}
      error={getErrorDetails(error, value, (newValue) =>
        onChange(row, column.id, newValue),
      )}
      invalid={invalid}
      selected={selected}
      warning={warning}
    >
      {propValue ? (
        <RelationPill
          fallback={<JSONPill type={type} value={value} />}
          schema={relation}
          value={propValue}
          onDelete={() => onChange(row, column.id, null)}
        />
      ) : (
        <RelationCellDropdown
          isOpen={selected && !editing}
          relation={relation}
          onFill={(newValue) => onChange(row, column.id, newValue)}
        />
      )}
    </CellWrapper>
  );

  const propertyChangeHandler = (propertyName: string, value: JSONValue) => {
    const path = [
      column.id,
      currentEditor.path === ROOT_PATH ? undefined : currentEditor.path,
      propertyName,
    ]
      .filter(Boolean)
      .join(".");

    onChange(row, path, value);
  };

  return (
    <FloatingWindow
      button={children}
      isOpen={propValue && editing}
      maximizable={false}
      title={
        <div className="flex items-start">
          {pool.map((editor, index) => (
            <div
              key={editor.relation + editor.path}
              className="flex items-center"
            >
              <RelationPill
                fallback={<JSONPill type={type} value={value} />}
                schema={editor.relation}
                value={
                  editor.path === ROOT_PATH
                    ? propValue
                    : get(propValue, editor.path)
                }
                onClick={
                  index !== pool.length - 1
                    ? () => goToRelation(editor.relation, editor.path)
                    : undefined
                }
              />
              {index !== pool.length - 1 && (
                <Icon icon={faChevronRight} size="2xs" />
              )}
            </div>
          ))}
        </div>
      }
      onClose={() => reset("editingCellId")}
    >
      <div className="decideScrollbar mt-2 flex h-full max-h-[560px] flex-1 flex-col gap-y-1.5 overflow-hidden py-2">
        {relationSchema ? (
          <div className="isolate flex flex-col divide-y divide-gray-100 border border-gray-100">
            {properties.map((key) => {
              const property = relationSchema.properties[key];
              return (
                <div
                  key={key}
                  className={twJoin(
                    "flex h-8 items-stretch divide-x divide-gray-100",
                    (isEditing(key) || isSelected(key)) && "z-10",
                  )}
                >
                  <div className="w-5/12 flex-none pr-8 pl-1">
                    <PropertyName property={property} />
                  </div>
                  <div
                    className="w-7/12 flex-1"
                    onClickCapture={() => {
                      // Has to use capture to because the popover component
                      // down below prevents the click event from bubbling up
                      if (!isSelected(key)) {
                        setSelectedProperty(key);
                      }
                    }}
                    onDoubleClick={() => setEditingProperty(key)}
                  >
                    <EntityPropertyEditorCell
                      disabled={disabled}
                      editing={isEditing(key)}
                      enumValues={property._values?.map((v) =>
                        JSON.stringify(v._internal_name),
                      )}
                      property={property}
                      propertyKey={key}
                      selected={isSelected(key)}
                      value={get(
                        propValue ?? {},
                        currentEditor.path === ROOT_PATH
                          ? key
                          : `${currentEditor.path}.${key}`,
                      )}
                      onBlur={() => setSelectedProperty(key)}
                      onChange={propertyChangeHandler}
                      onGoToRelation={(relation, path) => {
                        goToRelation(
                          relation,
                          currentEditor.path !== ROOT_PATH
                            ? `${currentEditor.path}.${path}`
                            : path,
                        );
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </FloatingWindow>
  );
};

const PropertyName: React.FC<{ property: EntitySchemaProperty }> = ({
  property,
}) => {
  return (
    <div className="flex h-full items-center gap-x-1 font-inter-medium-12px text-gray-800">
      <TypeIcon cardinality={property._cardinality} type={property._type} />
      <Tooltip title={property._display_name} asChild>
        <p className="truncate capitalize">{property._display_name}</p>
      </Tooltip>
    </div>
  );
};

const usePropertyCellState = () => {
  const [editing, setEditing] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const handleSelect = (value: string) => {
    setSelected(value);
    setEditing(null);
  };

  const handleEditing = (value: string) => {
    setSelected(value);
    setEditing(value);
  };

  const reset = useCallback(() => {
    setEditing(null);
    setSelected(null);
  }, []);

  const isEditing = (value: string) => editing === value;
  const isSelected = (value: string) => selected === value;

  return {
    setSelected: handleSelect,
    setEditing: handleEditing,
    reset,
    isEditing,
    isSelected,
  } as const;
};

type EditorRel = {
  relation: string;
  path: string;
};

const ROOT_PATH = "__root__";

const useEntitiesPool = (relation: string) => {
  const [pool, setPool] = useState<EditorRel[]>([
    {
      relation,
      path: ROOT_PATH,
    },
  ]);

  const currentEditor = last(pool) as EditorRel;

  const goToRelation = (relation: string, path: string) => {
    const existingRelation = pool.findIndex(
      (r) => r.relation === relation && r.path === path,
    );
    // If no relation, we go one level deeper
    if (existingRelation === -1) {
      setPool([
        ...pool,
        {
          relation,
          path,
        },
      ]);
    } else {
      // If relation already exists, we go to it
      setPool(pool.slice(0, existingRelation + 1));
    }
  };

  const reset = useCallback(() => {
    setPool([
      {
        relation,
        path: ROOT_PATH,
      },
    ]);
  }, [relation]);

  return { pool, goToRelation, currentEditor, reset } as const;
};
